"use client";

import Link from "next/link";

import styles from "./card-variant-03.module.scss";
import { PlatformsImage } from "@/components/package-components/non-editable/core";
import { VehicleCardVariantProps } from "../props";
import Chip from "@/components/package-components/non-editable/misc/chip";
import { Share2 } from "lucide-react";
import { ModalForm } from "../../../details-1/variants/brand-new/01/form";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { ContactActionProps } from "../../../details-1/props";
import { removeNonNumericAndSpaces } from "@/components/package-components/utils";
import { BuyerPreferenceEnum, SellerTypeEnum } from "../../../../../../types";
import { useCookieListener } from "@/components/package-components/hooks/useCookieListener";

export default function VehicleCardVariant03({
  vehicle,
  priority,
  options,
  adjustHeightBasedOnOptions,
  imageThumb,
  vehiclePrice,
  formatedSlug,
  id,
  props,
}: VehicleCardVariantProps) {
  const [open, setOpen] = useState<boolean>(false);
  const [showPriceFromCookie, setShowPriceFromCookie] =
    useState<boolean>(false);

  const { cookieValue: buyerPreferenceRaw } = useCookieListener(
    "BuyerPreference",
    { listen: true, intervalMs: 500 }
  );

  const sellerType = props?.data.site?.sellerType;
  const showDataLayerForm =
    typeof props?.options.showDataLayerForm === "object"
      ? props?.options.showDataLayerForm.value
      : props?.options.showDataLayerForm ?? false;

  const contactActions = Array.isArray(props?.contactActions)
    ? props?.contactActions
    : //@ts-ignore
      props?.contactActions.items;
  const whatsappAction = contactActions.find(
    (i: ContactActionProps) => i.actionType === "whatsApp"
  );

  const vendorWhatsappPhone =
    (whatsappAction?.link?.value || whatsappAction?.link) ?? "";

  useEffect((): void => {
    const showPriceCookie: string | undefined = Cookies.get("showPrice");
    if (showPriceCookie === "true") {
      setShowPriceFromCookie(true);
    }
  }, []);

  const features = vehicle.vehicleZeroKMOutput?.files
    .filter((file) => file.category === 4 && file.title && file.description)
    .map((i) => i.title)
    .join(",");

  async function handleShare() {
    await navigator.share({ url: formatedSlug });
  }

  const inventory0KM =
    sellerType === SellerTypeEnum.ONLY_ZERO_KM ||
    buyerPreferenceRaw === BuyerPreferenceEnum.ONLY_ZERO_KM;

  const handleCardTitle = () => {
    switch (inventory0KM) {
      case true:
        return (
          <>
            {vehicle.make}{" "}
            <span className={`${styles.highlight} highlight-${id} mt-2`}>
              {vehicle.model}
            </span>
          </>
        );

      case false:
        return (
          <>
            {vehicle.make}{" "}
            <span className={`${styles.highlight} highlight-${id} mt-2`}>
              {vehicle.model}
            </span>
          </>
        );
    }
  };

  const handleTalkNow = () => {
    if (showDataLayerForm) {
      setOpen(true);
      return;
    }

    let localUrl = "";

    if (typeof window != "undefined") {
      localUrl = window.location.href;
    }

    if (vendorWhatsappPhone && vendorWhatsappPhone != "") {
      const defaultMessage = `Olá, gostaria de saber mais sobre esse veículo que estou vendo no seu site:

${localUrl}`;

      const encodedFullMessage = encodeURIComponent(defaultMessage);

      window.open(
        `https://wa.me/55${removeNonNumericAndSpaces(
          String(vendorWhatsappPhone)
        )}?text=→${encodedFullMessage}`,
        "_blank"
      );
    }
  };

  const handlePrice = () => {
    switch (inventory0KM) {
      case true:
        return (
          <div className={`${styles["text-wrapper"]}} text-wrapper-${id}`}>
            <p className={`flex gap-2 ${styles.textAboveVehiclePrice}`}>
              A partir de{" "}
              <span className={`${styles.price} price-${id}`}>R$</span>{" "}
              {(vehicle.minPrice ?? 0).toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
        );

      default:
        return (
          <div className={`${styles["text-wrapper"]}} text-wrapper-${id}`}>
            <p className={styles.textAboveVehiclePrice}>
              <span className={`${styles.price} price-${id}`}>R$</span>{" "}
              {vehiclePrice}
            </p>
          </div>
        );
    }
  };

  return (
    <>
      <Link
        href={formatedSlug}
        className={`${styles.card} ${
          adjustHeightBasedOnOptions ? styles["card--adjust-height"] : ""
        }`}
        prefetch
        onClick={(event) => {
          if (
            (event.target as HTMLElement).closest(".share-button") ||
            (event.target as HTMLElement).closest(".contact-button")
          ) {
            event.preventDefault();
          }
        }}
      >
        <PlatformsImage
          width={500}
          height={500}
          src={imageThumb}
          alt={`Imagem principal do veículo`}
          // className={`${styles.image2} image-${id}`}
          className={`${
            vehicle.vehicleZeroKMOutput ? styles.image : styles.image2
          } image-${id}`}
          priority={priority}
          loading={priority ? "eager" : "lazy"}
        />
        <div className={`${styles.content} content-${id} relative py-4 px-4`}>
          <div className="mb-3">
            <p className={styles.cardTitle}>
              {(vehicle?.type ?? "")?.toLowerCase() === "car" ? (
                handleCardTitle()
              ) : (
                <span>{vehicle.make}</span>
              )}
            </p>
            <p
              className={`${styles.description} description-${id} mt-[3px] !text-[16px]`}
            >
              {(vehicle?.type ?? "")?.toLowerCase() === "car" && !inventory0KM
                ? vehicle.version
                : vehicle.manufacturerDescription
                ? vehicle.manufacturerDescription
                : vehicle.model}
            </p>
          </div>
          <div className={`${styles["text-wrapper"]} text-wrapper-${id}`}>
            <button
              className={`share-button border-none bg-transparent rounded-full hover:bg-white/45 pl-[2px] pt-[3px] pr-[4.5px] pb-[3px] absolute top-2 md:top-4 right-2 md:right-4 z-50 flex justify-center items-center transition-colors`}
              onClick={handleShare}
              aria-label="Compartilhar Veículo"
            >
              <Share2 strokeWidth={1} />
            </button>
            <div className={`${styles["chip_row"]} chip_row-${id} mb-3`}>
              {(vehicle?.features || features)
                ?.split(",")
                ?.filter((_, idx) => idx < 5)
                .map((feat, index) => (
                  <Chip textOnly textWrap key={feat + index}>
                    • {feat}
                  </Chip>
                ))}
            </div>
            {vehiclePrice &&
              (Boolean(
                options.showVehiclePrice?.value ?? options.showVehiclePrice
              ) ||
                showPriceFromCookie) &&
              handlePrice()}

            <div className={`${styles.row} row-${id}`}>
              <div className="flex">
                {Boolean(
                  options.showYearFilter?.value ?? options.showYearFilter
                ) && (
                  <p>
                    {" "}
                    {vehicle.vehicleZeroKMOutput || vehicle.minPrice
                      ? ""
                      : `${vehicle.manufactureYear}/`}
                    {vehicle.modelYear}
                  </p>
                )}
                <p className="mx-2">-</p>
                {Boolean(
                  options.showKmFilter?.value ?? options.showKmFilter
                ) && (
                  <p>
                    {" "}
                    {Intl.NumberFormat("pt-BR", {
                      maximumFractionDigits: 0,
                      minimumFractionDigits: 0,
                    }).format(Number(vehicle.kilometers))}{" "}
                    km
                  </p>
                )}
              </div>
              <div className="!w-full flex justify-end items-center">
                <button
                  type="button"
                  onClick={handleTalkNow}
                  className="contact-button bg-primary-800 rounded-lg px-4 py-2 text-white text-sm font-semibold"
                >
                  Falar agora
                </button>
              </div>
            </div>

            {Boolean(
              options.showVehiclePrice?.value ?? options.showVehiclePrice
            ) === false &&
              String(
                options.textAboveVehiclePrice?.value ??
                  options.textAboveVehiclePrice
              ) !== "" &&
              options.textAboveVehiclePrice !== undefined && (
                <div
                  className={`${styles["text-wrapper"]}} text-wrapper-${id}`}
                >
                  <p className={styles.textAboveVehiclePrice}>
                    {String(
                      options.textAboveVehiclePrice?.value ??
                        options.textAboveVehiclePrice
                    )}
                  </p>
                </div>
              )}
          </div>
        </div>
      </Link>
      {props && (
        <ModalForm
          open={open}
          props={props}
          setOpen={setOpen}
          vehicle={vehicle}
        />
      )}
    </>
  );
}
